import { medisApi } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { unwrapMedis, type MedisEnvelope } from '@/lib/medis/envelope';

/**
 * MEDIS booking calls — `POST /Timetable` (available slots per month) and
 * `POST /SetAppointment` (create the appointment). Ported from the mobile
 * `MedisService`. SetAppointment is asynchronous server-side and answers with a
 * chain of `307` redirects to a polling URL; the dev proxy follows them
 * server-side (see vite.config.ts) so the browser only ever sees the final 200.
 */

// ─── Timetable ──────────────────────────────────────────────────────────────

export interface TimetableInterval {
  start: Date;
  end: Date;
  available: boolean;
}

export interface TimetableDay {
  day: Date;
  /** MEDIS entity UUID for this day's slots. */
  entityId: string;
  locationId: string | null;
  /** MEDIS physician UUID (Azure misspells the field `PhisicianID`). */
  physicianId: string | null;
  intervals: TimetableInterval[];
}

interface RawInterval {
  Start: string;
  End: string;
  Available?: boolean;
}
interface RawDay {
  Day: string;
  EntityID?: string;
  LocationID?: string;
  PhisicianID?: string;
  Available?: RawInterval[];
}
interface TimetableResponseDto {
  Days?: RawDay[];
}

export interface GetTimetableParams {
  physicianId?: string | null;
  specialityId?: string | null;
  locationId?: string | null;
  entityId?: string | null;
  /** `YYYY-MM-01`. */
  refMonth: string;
}

/** Build a local `Date` for a `HH:mm:ss` time on a given day (no timezone shift). */
function timeOnDay(day: Date, hms: string): Date {
  const [h, m, s] = hms.split(':').map((v) => Number(v));
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), h || 0, m || 0, s || 0);
}

function mapDay(raw: RawDay): TimetableDay | null {
  if (!raw.Day) return null;
  const day = new Date(raw.Day);
  if (Number.isNaN(day.getTime())) return null;
  const intervals = (raw.Available ?? [])
    .map((i) => ({ start: timeOnDay(day, i.Start), end: timeOnDay(day, i.End), available: i.Available !== false }))
    .filter((i) => !Number.isNaN(i.start.getTime()) && !Number.isNaN(i.end.getTime()));
  return {
    day,
    entityId: raw.EntityID ?? '',
    locationId: raw.LocationID ?? null,
    physicianId: raw.PhisicianID ?? null,
    intervals,
  };
}

export async function getTimetable(params: GetTimetableParams): Promise<TimetableDay[]> {
  const body: Record<string, unknown> = {};
  if (params.physicianId) body.PhysicianID = params.physicianId;
  if (params.entityId) body.EntityID = params.entityId;
  if (params.specialityId) body.SpecialityID = params.specialityId;
  if (params.locationId) body.LocationID = params.locationId;
  if (params.refMonth) body.RefMonth = params.refMonth;

  const { data } = await medisApi.post<MedisEnvelope<TimetableResponseDto>>(endpoints.medis.timetable, body);
  const response = unwrapMedis(data);
  return (response.Days ?? []).map(mapDay).filter((d): d is TimetableDay => d !== null);
}

// ─── SetAppointment ───────────────────────────────────────────────────────

export type SetAppointmentState =
  | 'saved'
  | 'emailedToCC'
  | 'timeOut'
  | 'overlapped'
  | 'invalid'
  | 'classifiedLists'
  | 'error';

export interface SetAppointmentInput {
  patientId: string;
  /** MEDIS physician UUID. */
  physicianId: string;
  /** MEDIS entity UUID (from the chosen timetable day). */
  entityId: string;
  /** MEDIS location UUID. */
  locationId: string;
  /** MEDIS investigation UUID, optional. */
  investigationId?: string | null;
  timeFrom: Date;
  timeTo: Date;
  appointmentNote: string;
  firstAvailable: boolean;
}

export interface SetAppointmentResult {
  state: SetAppointmentState;
  success: boolean;
  message?: string;
  appointmentId?: string;
}

interface SetAppointmentResponseDto {
  State?: string;
  Message?: string;
  AppointmentID?: string;
  Success?: boolean;
  Steps?: number;
}

function mapState(value: string): SetAppointmentState {
  switch (value.toLowerCase()) {
    case 'saved':
      return 'saved';
    case 'emailedtocc':
      return 'emailedToCC';
    case 'timeout':
      return 'timeOut';
    case 'overlapped':
      return 'overlapped';
    case 'invalid':
      return 'invalid';
    case 'classifiedlists':
      return 'classifiedLists';
    default:
      return 'error';
  }
}

/** Local naive ISO (`YYYY-MM-DDTHH:mm:ss`) — matches the app's `DateTime.toIso8601String()`. */
function toNaiveIso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export async function setAppointment(input: SetAppointmentInput): Promise<SetAppointmentResult> {
  const body = {
    PatientID: input.patientId,
    PhysicianID: input.physicianId,
    EntityID: input.entityId,
    LocationID: input.locationId,
    InvestigationID: input.investigationId ?? null,
    TimeFrom: toNaiveIso(input.timeFrom),
    TimeTo: toNaiveIso(input.timeTo),
    AppointmentNote: input.appointmentNote,
    FirstAvailable: input.firstAvailable,
  };

  const { data } = await medisApi.post<MedisEnvelope<SetAppointmentResponseDto>>(
    endpoints.medis.setAppointment,
    body,
  );
  // `unwrapMedis` throws if the outer envelope State !== success.
  const response = unwrapMedis(data);
  const state = mapState(response.State ?? 'Error');
  return {
    state,
    success: state === 'saved' || state === 'emailedToCC',
    message: response.Message ?? undefined,
    appointmentId: response.AppointmentID,
  };
}

/** User-facing message per outcome (ported from the app's `SetAppointmentState`). */
export const SET_APPOINTMENT_MESSAGES: Record<SetAppointmentState, string> = {
  saved: 'Programarea dumneavoastră a fost confirmată cu succes! Vă așteptăm la consultație.',
  emailedToCC:
    'Nu am putut finaliza programarea automat. Vă rugăm să sunați la call center 021 323 0000 pentru a clarifica programarea.',
  timeOut: 'Sistemul întâmpină o întârziere în acest moment. Vă rugăm să încercați din nou peste câteva minute.',
  overlapped: 'Intervalul orar selectat nu mai este disponibil. Vă rugăm să alegeți o altă oră.',
  invalid:
    'Solicitarea nu a putut fi procesată din cauza unor date incorecte. Verificați informațiile și începeți o programare nouă.',
  classifiedLists:
    'Solicitarea nu a putut fi procesată din cauza unor date incorecte. Verificați informațiile și începeți o programare nouă.',
  error: 'A intervenit o eroare neașteptată. Vă rugăm să încercați din nou peste câteva minute.',
};
